import { isEmpty, isNil } from 'lodash';

import { isNilOrEmpty } from '../objectUtils';

export enum BankTransactionNature {
  CARD_PAYMENT = 'CARD_PAYMENT',
  CARD_SETTLEMENT = 'CARD_SETTLEMENT',
  CASH_DEPOSIT = 'CASH_DEPOSIT',
  CASH_WITHDRAWAL = 'CASH_WITHDRAWAL',
  CHEQUE = 'CHEQUE',
  CREDIT_TRANSFER = 'CREDIT_TRANSFER',
  DIRECT_DEBIT = 'DIRECT_DEBIT',
  FEES = 'FEES',
  INTEREST = 'INTEREST',
  LOAN = 'LOAN',
  RETURN = 'RETURN',
  TAX = 'TAX',
  UNKNOWN = 'UNKNOWN',
}

export interface BankTransactionCodes {
  bankTransactionCode?: string | null;
  proprietaryBankTransactionCode?: string | null;
}

const ISO_CODE_SEPARATOR = /[-/]/;

const ISO_SUB_FAMILY_NATURES: Record<string, BankTransactionNature> = {
  CCHQ: BankTransactionNature.CHEQUE,
  CDPT: BankTransactionNature.CASH_DEPOSIT,
  CHRG: BankTransactionNature.FEES,
  COMM: BankTransactionNature.FEES,
  CWDL: BankTransactionNature.CASH_WITHDRAWAL,
  INTR: BankTransactionNature.INTEREST,
  POSC: BankTransactionNature.CARD_PAYMENT,
  POSD: BankTransactionNature.CARD_PAYMENT,
  POSP: BankTransactionNature.CARD_PAYMENT,
  RPCR: BankTransactionNature.RETURN,
  RRTN: BankTransactionNature.RETURN,
  SMRT: BankTransactionNature.CARD_PAYMENT,
  TAXE: BankTransactionNature.TAX,
  UPCT: BankTransactionNature.CARD_PAYMENT,
  UPDD: BankTransactionNature.RETURN,
  URDD: BankTransactionNature.RETURN,
};

const ISO_DOMAIN_NATURES: Record<string, BankTransactionNature> = {
  LDAS: BankTransactionNature.LOAN,
};

const ISO_FAMILY_NATURES: Record<string, BankTransactionNature> = {
  CCRD: BankTransactionNature.CARD_PAYMENT,
  ICDT: BankTransactionNature.CREDIT_TRANSFER,
  ICHQ: BankTransactionNature.CHEQUE,
  IDDT: BankTransactionNature.DIRECT_DEBIT,
  MCRD: BankTransactionNature.CARD_PAYMENT,
  RCDT: BankTransactionNature.CREDIT_TRANSFER,
  RCHQ: BankTransactionNature.CHEQUE,
  RDDT: BankTransactionNature.DIRECT_DEBIT,
};

export const buildIsoBankTransactionCode = (
  domain?: string | null,
  family?: string | null,
  subFamily?: string | null
): string | undefined => {
  const parts = [domain, family, subFamily].reduce<string[]>((acc, part) => {
    const cleanedPart = part?.trim().toUpperCase() ?? '';
    return isEmpty(cleanedPart) ? acc : [...acc, cleanedPart];
  }, []);
  return isEmpty(parts) ? undefined : parts.join('-');
};

const getIsoNature = (bankTransactionCode?: string | null): BankTransactionNature => {
  if (isNilOrEmpty(bankTransactionCode)) {
    return BankTransactionNature.UNKNOWN;
  }
  const parts = bankTransactionCode
    .toUpperCase()
    .split(ISO_CODE_SEPARATOR)
    .map((part) => part.trim());
  const subFamilyNature = ISO_SUB_FAMILY_NATURES[parts[parts.length - 1]];
  if (!isNil(subFamilyNature)) {
    return subFamilyNature;
  }
  const familyPart = parts.find((part) => !isNil(ISO_FAMILY_NATURES[part]));
  if (!isNil(familyPart)) {
    return ISO_FAMILY_NATURES[familyPart];
  }
  return ISO_DOMAIN_NATURES[parts[0]] ?? BankTransactionNature.UNKNOWN;
};

// A Belgian CODA transaction code is type (1 digit) + family (2) + operation (2) + category (3),
// and banks hand it over with or without the leading type and the trailing category
const CODA_FAMILY_OFFSET_BY_LENGTH: Record<number, number> = { 4: 0, 5: 1, 7: 0, 8: 1 };

const CODA_FAMILY_NATURES: Record<string, BankTransactionNature> = {
  '01': BankTransactionNature.CREDIT_TRANSFER,
  '03': BankTransactionNature.CHEQUE,
  '04': BankTransactionNature.CARD_PAYMENT,
  '05': BankTransactionNature.DIRECT_DEBIT,
  '13': BankTransactionNature.LOAN,
  '41': BankTransactionNature.CREDIT_TRANSFER,
  '43': BankTransactionNature.CHEQUE,
  '80': BankTransactionNature.FEES,
};

const CODA_CARD_OPERATION_NATURES: Record<string, BankTransactionNature> = {
  '02': BankTransactionNature.CARD_PAYMENT,
  '03': BankTransactionNature.CARD_SETTLEMENT,
  '04': BankTransactionNature.CASH_WITHDRAWAL,
  '06': BankTransactionNature.CARD_PAYMENT,
  '07': BankTransactionNature.CARD_PAYMENT,
  '08': BankTransactionNature.CARD_PAYMENT,
  '53': BankTransactionNature.CASH_DEPOSIT,
};

const CODA_COUNTER_OPERATION_NATURES: Record<string, BankTransactionNature> = {
  '01': BankTransactionNature.CASH_WITHDRAWAL,
  '13': BankTransactionNature.CASH_WITHDRAWAL,
  '50': BankTransactionNature.CASH_DEPOSIT,
};

const CODA_OPERATION_NATURES: Record<string, Record<string, BankTransactionNature>> = {
  '04': CODA_CARD_OPERATION_NATURES,
  '09': CODA_COUNTER_OPERATION_NATURES,
};

const getCodaNature = (proprietaryBankTransactionCode?: string | null): BankTransactionNature => {
  if (isNilOrEmpty(proprietaryBankTransactionCode)) {
    return BankTransactionNature.UNKNOWN;
  }
  const digits = proprietaryBankTransactionCode.replace(/\D/g, '');
  const familyOffset = CODA_FAMILY_OFFSET_BY_LENGTH[digits.length];
  if (isNil(familyOffset)) {
    return BankTransactionNature.UNKNOWN;
  }
  const family = digits.substring(familyOffset, familyOffset + 2);
  const operation = digits.substring(familyOffset + 2, familyOffset + 4);
  return CODA_OPERATION_NATURES[family]?.[operation] ?? CODA_FAMILY_NATURES[family] ?? BankTransactionNature.UNKNOWN;
};

const CARD_RELATED_NATURES = [
  BankTransactionNature.CARD_PAYMENT,
  BankTransactionNature.CARD_SETTLEMENT,
  BankTransactionNature.CASH_DEPOSIT,
  BankTransactionNature.CASH_WITHDRAWAL,
];

export const getBankTransactionNature = ({
  bankTransactionCode,
  proprietaryBankTransactionCode,
}: BankTransactionCodes): BankTransactionNature => {
  const isoNature = getIsoNature(bankTransactionCode);
  const codaNature = getCodaNature(proprietaryBankTransactionCode);
  if (isoNature === BankTransactionNature.UNKNOWN) {
    return codaNature;
  }
  // ISO 20022 keeps a card payment, an ATM withdrawal and the monthly card settlement in the same
  // family, where the bank's own code tells them apart
  if (isoNature === BankTransactionNature.CARD_PAYMENT && CARD_RELATED_NATURES.includes(codaNature)) {
    return codaNature;
  }
  return isoNature;
};
